import { User } from '@prisma/client'
import { Divider, Form, Input, Switch, Typography } from 'antd'
import React from 'react'
import { CommonClient } from '~/agents/common/client'
import { AppSessionType } from '~/features/appSession/common'
import { FeatureFormConfigurationClient } from '~/features/formConfiguration/client'
import { IconText } from '~/features/shared/icons'
import { Toolbox } from '~/plugins/toolbox/shared'
import { OnboardingValues } from '../../common'

type Props = {
  user: User
  appSession: AppSessionType
}

export const Settings: React.FC<Props> = ({ user, appSession }) => {
  const validateSocialUrl = (_: any, value: string) => {
    if (
      value?.includes('instagram.com') ||
      value?.includes('linkedin.com') ||
      value?.includes('facebook.com') ||
      value?.includes('x.com') ||
      value?.includes('google.com') ||
      !value
    ) {
      return Promise.resolve()
    }
    return Promise.reject(
      new Error(
        'Please enter a valid social media URL (Instagram, LinkedIn, Facebook, X or Google Business Profile)',
      ),
    )
  }

  const configuration = { timezone: user.timezone }

  const values = (appSession?.values?.onboarding ?? {}) as OnboardingValues

  const valuesSafe = {
    ...values,
    post: {
      ...values.post,
      isActive: Toolbox.Utility.isDefined(values.post?.rule),
    },
  } as OnboardingValues

  const logic = CommonClient.Form.useLogic<OnboardingValues>({
    appSession,
    cleanValues(values) {
      const cleaned = { ...values }

      // Clean post section if not active
      if (!cleaned.post.isActive) {
        cleaned.post = { isActive: false }
      }

      return cleaned
    },
  })

  const handleSubmit = async (valuesUpdated: OnboardingValues) => {
    const socialAccountUrlBefore = values.tone?.socialAccountUrl ?? null
    const socialAccountUrlAfter = valuesUpdated.tone?.socialAccountUrl ?? null

    const isSocialAccountUrlChanged =
      socialAccountUrlBefore !== socialAccountUrlAfter

    if (
      isSocialAccountUrlChanged &&
      Toolbox.Utility.isDefined(socialAccountUrlAfter)
    ) {
      try {
        await validateSocialUrl(null, socialAccountUrlAfter)

        await logic.triggerHttpEvent('onboarding.tone.socialAccountUrlUpdated')
      } catch (error) {
        console.log('Error validating social account URL:', error)
      }
    }
  }

  return (
    <>
      <CommonClient.Form.Wrapper
        {...logic}
        values={valuesSafe}
        onSubmit={handleSubmit}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          Social Networks
        </Typography.Title>

        <Divider className="my-3" />

        <CommonClient.Form.InputWrapper
          label={
            <>
              <IconText name="linkedin" /> Linkedin
            </>
          }
          description="Generate posts for LinkedIn"
        >
          <Form.Item name={['platform', 'linkedin']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <CommonClient.Form.InputWrapper
          label={
            <>
              <IconText name="facebook" /> Facebook
            </>
          }
          description="Generate posts for Facebook"
        >
          <Form.Item name={['platform', 'facebook']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <CommonClient.Form.InputWrapper
          label={
            <>
              <IconText name="instagram" /> Instagram
            </>
          }
          description="Generate posts for Instagram"
        >
          <Form.Item name={['platform', 'instagram']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <CommonClient.Form.InputWrapper
          label={
            <>
              <IconText name="x-square" /> Twitter
            </>
          }
          description="Generate posts for Twitter"
        >
          <Form.Item name={['platform', 'twitter']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <CommonClient.Form.InputWrapper
          label={
            <>
              <IconText name="google" /> Google Business Profile
            </>
          }
          description="Generate posts for Google Business Profile"
        >
          <Form.Item name={['platform', 'gmb']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <Typography.Title level={4} style={{ margin: 0, marginTop: 32 }}>
          Tone
        </Typography.Title>

        <Divider className="my-3" />

        <Typography.Text type="secondary">
          Used to analyze your tone and generate more accurate posts
        </Typography.Text>
        <Form.Item
          name={['tone', 'socialAccountUrl']}
          rules={[
            { validator: validateSocialUrl },
            {
              type: 'url',
              message: 'This input must be a valid url.',
            },
          ]}
          className="mt-2"
        >
          <Input placeholder="https://www.instagram.com/yourhandle" />
        </Form.Item>

        <Typography.Title level={4} style={{ margin: 0, marginTop: 32 }}>
          Daily Posts
        </Typography.Title>

        <Divider className="my-3" />

        <CommonClient.Form.InputWrapper
          label="Daily Posts"
          description="Receive daily list of posts ideas"
        >
          <Form.Item name={['post', 'isActive']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <Form.Item<OnboardingValues>
          shouldUpdate={(valuesBefore, valuesAfter) =>
            valuesBefore?.post?.isActive !== valuesAfter?.post?.isActive
          }
        >
          {instance => {
            const isActive = instance.getFieldValue(['post', 'isActive'])

            if (!isActive) {
              return <></>
            }

            return (
              <CommonClient.Form.InputWrapper
                label="Message time"
                description="Choose at what time you want to receive the list"
              >
                <Form.Item
                  name={['post', 'rule']}
                  getValueFromEvent={items => items[0]}
                >
                  <FeatureFormConfigurationClient.RRulePicker
                    type="DAILY"
                    formValuesConfiguration={configuration}
                  />
                </Form.Item>
              </CommonClient.Form.InputWrapper>
            )
          }}
        </Form.Item>
      </CommonClient.Form.Wrapper>
    </>
  )
}
